import { type Event } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  DataTablePagination,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@kalos/ui';
import { ChevronDownIcon, ExclamationTriangleIcon, FileTextIcon } from '@radix-ui/react-icons';
import { format, isValid, parseISO } from 'date-fns';
import { type FC, useMemo, useState } from 'react';

import { JOB_STATUS_COLORS, ROWS_PER_PAGE } from '../../../../constants';
import { formatTimeRangeCondensed } from '../../../../tools/formatTimeUtils';
import { JobHistoryDetail } from './JobHistoryDetail';

interface PropertyHistoryProps {
  eventsData?: { results: Event[] };
  isLoading?: boolean;
  error?: Error | null;
  currentPage?: number;
  onPageChange?: React.Dispatch<React.SetStateAction<number>>;
}

// Get status badge style using JOB_STATUS_COLORS
const getStatusStyle = (logJobStatus: string) => {
  const status = logJobStatus || 'Requested';
  const hexColor = JOB_STATUS_COLORS[status] || JOB_STATUS_COLORS['Requested'];
  const isDarkStatus = ['Canceled', 'Archived'].includes(status);

  return {
    backgroundColor: `#${hexColor}`,
    color: isDarkStatus ? '#fff' : '#000',
  };
};

// Format a job's start–end window (e.g. "9am–5pm"), or null when either time is unset.
const formatJobTimeRange = (timeStarted: string, timeEnded: string): string | null => {
  const isUnset = (time: string) => !time || /^0{1,2}:00(:00)?$/.test(time.trim());
  if (isUnset(timeStarted) || isUnset(timeEnded)) return null;
  return formatTimeRangeCondensed(timeStarted, timeEnded);
};

export const PropertyHistory: FC<PropertyHistoryProps> = ({
  eventsData,
  isLoading,
  error,
  currentPage: currentPageProp,
  onPageChange: onPageChangeProp,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('__all__');
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [openJobId, setOpenJobId] = useState<number | null>(null);
  const [internalPage, setInternalPage] = useState(0);

  // Use prop values if provided, otherwise use internal state
  const currentPage = currentPageProp ?? internalPage;
  const onPageChange = onPageChangeProp ?? setInternalPage;

  const { filteredEvents, paginatedEvents, totalPages } = useMemo(() => {
    if (!eventsData?.results) return { filteredEvents: [], paginatedEvents: [], totalPages: 0 };

    let filtered = eventsData.results.filter((event) => event.id !== 0);

    if (searchTerm) {
      const lowercasedFilter = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (event) =>
          event.id?.toString().includes(lowercasedFilter) ||
          event.description?.toLowerCase().includes(lowercasedFilter),
      );
    }

    if (filterType !== '__all__') {
      filtered = filtered.filter((event) => event.jobType === filterType);
    }

    if (filterStatus !== '__all__') {
      filtered = filtered.filter((event) => (event.logJobStatus || 'Pending') === filterStatus);
    }

    const sorted = filtered.sort((a, b) => {
      const dateA = a.dateStarted ? new Date(a.dateStarted) : new Date(0);
      const dateB = b.dateStarted ? new Date(b.dateStarted) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });

    const pages = Math.ceil(sorted.length / ROWS_PER_PAGE);
    const startIndex = currentPage * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    const paginated = sorted.slice(startIndex, endIndex);

    return {
      filteredEvents: sorted,
      paginatedEvents: paginated,
      totalPages: pages,
    };
  }, [eventsData?.results, searchTerm, filterType, filterStatus, currentPage]);

  const { uniqueJobTypes, uniqueJobStatuses } = useMemo(() => {
    if (!eventsData?.results) return { uniqueJobTypes: [], uniqueJobStatuses: [] };
    const jobTypes = new Set<string>();
    const jobStatuses = new Set<string>();
    eventsData.results.forEach((event) => {
      if (event.jobType) jobTypes.add(event.jobType);
      jobStatuses.add(event.logJobStatus || 'Pending');
    });
    return {
      uniqueJobTypes: Array.from(jobTypes),
      uniqueJobStatuses: Array.from(jobStatuses),
    };
  }, [eventsData?.results]);

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="mb-2 text-red-600">Error Loading Property History</CardTitle>
            <CardDescription>
              There was an error loading the property history. Please try again.
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    );
  }

  const resetFilters = () => {
    setSearchTerm('');
    setFilterType('__all__');
    setFilterStatus('__all__');
    onPageChange(0);
  };

  return (
    <div className="space-y-4 p-4 sm:p-6">
      {/* Header Section */}
      <div className="space-y-4">
        <p className="text-xs text-gray-500 sm:text-sm">
          {isLoading ? 'Loading history...' : `Found ${filteredEvents.length} service records`}
        </p>

        {/* Filters Section */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:space-x-2">
          <Input
            placeholder="Search by Job # or Service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64"
            disabled={isLoading}
          />
          <Select value={filterType} onValueChange={setFilterType} disabled={isLoading}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Types</SelectItem>
              {uniqueJobTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus} disabled={isLoading}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Statuses</SelectItem>
              {uniqueJobStatuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(searchTerm || filterType !== '__all__' || filterStatus !== '__all__') && (
            <Button
              variant="outline"
              size="default"
              onClick={resetFilters}
              className="w-full sm:w-auto"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, idx) => (
            <Card key={idx} className="bg-white">
              <CardContent className="space-y-2 p-3 sm:p-4">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-24 rounded-full" />
                </div>
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-4 w-full" />
                <div className="flex gap-1.5">
                  <Skeleton className="h-5 w-16 rounded-md" />
                  <Skeleton className="h-5 w-20 rounded-md" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredEvents.length > 0 ? (
        <div className="space-y-3">
          {paginatedEvents.map((event) => (
            <div key={event.id}>
              <Card
                className="cursor-pointer bg-white transition-all focus-within:ring-2 focus-within:ring-purple-500 focus-within:ring-offset-2 hover:bg-gray-50 hover:shadow-md"
                onClick={() => setOpenJobId(openJobId === event.id ? null : event.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpenJobId(openJobId === event.id ? null : event.id);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-expanded={openJobId === event.id}
                aria-label={`Job #${event.id} - ${event.jobType || 'N/A'} - ${event.logJobStatus || 'Pending'}`}
              >
                <CardContent className="space-y-2 p-3 sm:p-4">
                  {/* Job number + status; chevron-only on the far right */}
                  <div className="flex items-start gap-2">
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="shrink-0 text-sm font-semibold text-gray-900 sm:text-base">
                        #{event.id}
                      </span>
                      <Badge
                        variant="default"
                        style={getStatusStyle(event.logJobStatus || 'Pending')}
                        className="max-w-full text-left text-xs leading-tight"
                      >
                        <span className="break-words">{event.logJobStatus || 'Pending'}</span>
                      </Badge>
                    </div>
                    <ChevronDownIcon
                      className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform sm:h-5 sm:w-5 ${
                        openJobId === event.id ? 'rotate-180' : ''
                      }`}
                    />
                  </div>

                  <p className="text-xs text-gray-500">
                    {(() => {
                      const parsedDate = event.dateStarted ? parseISO(event.dateStarted) : null;
                      const dateLabel =
                        parsedDate && isValid(parsedDate)
                          ? format(parsedDate, 'M/d/yyyy')
                          : 'No Date';
                      const timeRange = formatJobTimeRange(event.timeStarted, event.timeEnded);
                      return timeRange ? `${dateLabel} · ${timeRange}` : dateLabel;
                    })()}
                  </p>

                  {event.description && (
                    <p className="line-clamp-2 text-sm text-gray-800">{event.description}</p>
                  )}

                  {(event.jobType || event.jobSubtype) && (
                    <div className="flex flex-wrap gap-1.5">
                      {event.jobType && (
                        <Badge variant="outline" className="text-xs font-normal text-gray-600">
                          {event.jobType}
                        </Badge>
                      )}
                      {event.jobSubtype && (
                        <Badge variant="outline" className="text-xs font-normal text-gray-600">
                          {event.jobSubtype}
                        </Badge>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              {openJobId === event.id && <JobHistoryDetail event={event} />}
            </div>
          ))}
        </div>
      ) : (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <FileTextIcon className="h-6 w-6 text-gray-600" />
            </div>
            <CardTitle className="mb-2 text-gray-900">No Jobs Found</CardTitle>
            <CardDescription>
              {searchTerm || filterType !== '__all__' || filterStatus !== '__all__'
                ? 'No jobs match your current search criteria. Try adjusting your filters.'
                : 'There are no jobs associated with this property.'}
            </CardDescription>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-gray-600 sm:text-sm">
          {!isLoading && totalPages > 1 && (
            <>
              Showing {currentPage * ROWS_PER_PAGE + 1} to{' '}
              {Math.min((currentPage + 1) * ROWS_PER_PAGE, filteredEvents.length)} of{' '}
              {filteredEvents.length} jobs
            </>
          )}
        </p>
        {totalPages > 1 && (
          <DataTablePagination
            currentPage={currentPage}
            pageCount={totalPages}
            setPage={onPageChange}
            disabled={isLoading}
          />
        )}
      </div>
    </div>
  );
};
